import { type ReactNode, useId } from "react";
import { cn } from "../lib/cn";

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Render prop receives the ids to wire onto the control. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
  /** `row`: label in a fixed column on the left, control and hint on the right (settings forms). */
  layout?: "stack" | "row";
}

/** Label + control + hint/error, with the aria wiring done once. */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
  layout = "stack",
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;
  const message = error ? (
    <p id={errorId} className="text-sm text-err-fg">
      {error}
    </p>
  ) : hint ? (
    <p id={hintId} className="text-sm text-fg-subtle">
      {hint}
    </p>
  ) : null;
  if (layout === "row")
    return (
      <div
        className={cn(
          "grid grid-cols-[140px_minmax(0,1fr)] items-start gap-x-4 gap-y-1",
          className,
        )}
      >
        <label htmlFor={id} className="pt-1.5 text-base font-medium text-fg">
          {label}
          {required ? (
            <span className="ml-0.5 text-err-fg" aria-hidden>
              *
            </span>
          ) : null}
        </label>
        <div className="flex flex-col gap-1">
          {children({ id, describedBy, invalid: Boolean(error) })}
          {message}
        </div>
      </div>
    );
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-base font-medium text-fg">
        {label}
        {required ? (
          <span className="ml-0.5 text-err-fg" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {message}
    </div>
  );
}
